import React, { useState } from "react";
import type { RecipeStep } from "@recipevault/shared";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import {
  initialWaitMinutes,
  nudgeMinutes,
  readyTimeIso,
  formatDuration,
} from "./waitMath";

interface WaitPickerProps {
  step: RecipeStep;
  onConfirm: (body: { pauseUntilMinutes: number; reason: string }) => void;
  onCancel: () => void;
}

export function WaitPicker({ step, onConfirm, onCancel }: WaitPickerProps) {
  const { minutes: initialMin, fromRecipe } = initialWaitMinutes(step);

  const [minutes, setMinutes] = useState<number>(initialMin);
  const [reason, setReason] = useState<string>(
    (step.text ?? "").slice(0, 200),
  );
  const [exactInput, setExactInput] = useState<string>("");

  function handleNudge(delta: number) {
    setMinutes((prev) => nudgeMinutes(prev, delta));
  }

  function handleExactChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    setExactInput(raw);
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed) && parsed > 0) {
      // Clamp via nudgeMinutes with delta=0 is achieved by clamping to bounds directly
      setMinutes(nudgeMinutes(parsed, 0));
    }
  }

  function handleConfirm() {
    onConfirm({ pauseUntilMinutes: minutes, reason });
  }

  // Display-only: local time when the wait will be over.
  // NOTE: this is purely for user convenience — the server is authoritative and
  // computes wakeUpAt from the submitted pauseUntilMinutes duration. This clock
  // value is never sent to the server.
  const readyIso = readyTimeIso(Date.now(), minutes);
  const readyDisplay = new Date(readyIso).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <Dialog open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Start the wait</DialogTitle>
        </DialogHeader>

        {/* Large duration readout */}
        <div className="flex items-center justify-center gap-3 py-4">
          <span
            className="text-5xl font-bold tabular-nums"
            aria-label={`Wait duration: ${formatDuration(minutes)}`}
          >
            {formatDuration(minutes)}
          </span>
          {fromRecipe && (
            <span
              className="rounded bg-muted px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              aria-label="Duration from recipe"
            >
              FROM RECIPE
            </span>
          )}
        </div>

        {/* Relative nudge buttons — ≥48px touch targets (cook size) */}
        <div className="flex justify-center gap-2">
          <Button
            size="cook"
            variant="outline"
            aria-label="Subtract 1 hour"
            onClick={() => handleNudge(-60)}
          >
            −1h
          </Button>
          <Button
            size="cook"
            variant="outline"
            aria-label="Subtract 30 minutes"
            onClick={() => handleNudge(-30)}
          >
            −30m
          </Button>
          <Button
            size="cook"
            variant="outline"
            aria-label="+30m"
            onClick={() => handleNudge(30)}
          >
            +30m
          </Button>
          <Button
            size="cook"
            variant="outline"
            aria-label="Add 1 hour"
            onClick={() => handleNudge(60)}
          >
            +1h
          </Button>
        </div>

        {/* Set exact minutes input */}
        <div className="flex items-center gap-2">
          <label
            htmlFor="wait-exact-minutes"
            className="shrink-0 text-sm text-muted-foreground"
          >
            Set exact…
          </label>
          <Input
            id="wait-exact-minutes"
            type="number"
            min={1}
            max={10080}
            value={exactInput}
            onChange={handleExactChange}
            placeholder="minutes"
            aria-label="Set exact duration in minutes"
            className="w-28"
          />
        </div>

        {/* Display-only ready-time line.
            NOTE: display only — the server is authoritative for wakeUpAt.
            We show local time here purely as a convenience for the cook. */}
        <p className="text-sm text-muted-foreground" aria-live="polite">
          Ready around{" "}
          <span className="font-medium text-foreground">{readyDisplay}</span>
        </p>

        {/* Reason text input */}
        <div className="flex flex-col gap-1">
          <label htmlFor="wait-reason" className="text-sm font-medium">
            Reason
          </label>
          <Input
            id="wait-reason"
            type="text"
            value={reason}
            maxLength={200}
            onChange={(e) => setReason(e.target.value.slice(0, 200))}
            aria-label="Wait reason"
            placeholder="Why are you waiting?"
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="cook"
            aria-label="Cancel"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            size="cook"
            aria-label="Start the wait"
            onClick={handleConfirm}
          >
            Start the wait
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
